import { useEffect, useRef, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { links } from '../api/endpoints.ts';
import { messengerName } from '../api/types.ts';
import { onlyDigits, SERBIA, splitPhone, type PhoneCountry } from '../core/phone-country.ts';
import { errorText, useLang, useStrings, type Lang } from '../i18n/index.ts';
import type { Strings } from '../i18n/strings.ts';
import { requestCode, verifyCode } from '../state/session.ts';
import { CountryCodeMenu } from '../ui/CountryCodeMenu.tsx';
import { Button, cx, ErrorBanner, FieldBox, GlassButton, LinkedText } from '../ui/kit.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import '../styles/account.css';

const RESEND_COOLDOWN_S = 60;

/** Часовой лимит кодов: сервер говорит, когда можно снова, и минутная пауза читается как минуты. */
function throttledTextOf(strings: Strings, failure: unknown): string | null {
  if (!(failure instanceof ApiError) || failure.code !== 'otp_throttled') return null;
  const seconds = Number(failure.details['retryAfterSeconds']);
  if (!Number.isFinite(seconds) || seconds <= RESEND_COOLDOWN_S) return null;
  return strings.otpThrottledFor(Math.ceil(seconds / 60));
}
const CODE_LENGTH = 6;
const MAX_PHONE_DIGITS = 12;

/** Знак и словесное написание TROTTY10 в одну строку. */
export function BrandLockup({ size = 44 }: { size?: number }) {
  return (
    <span className="brand-lockup" style={{ fontSize: Math.round(size * 0.55) }}>
      <img src={`${import.meta.env.BASE_URL}favicon.svg`} alt="" width={size} height={size} />
      <span>
        TROTTY<em>10</em>
      </span>
    </span>
  );
}

/**
 * Вход по одноразовому коду: номер телефона, затем шесть цифр. Пароля нет, поэтому поле кода
 * открывается только после удачной отправки, а повторы ограничивает сервер.
 *
 * Витрина открыта без аккаунта: лист поднимается на оформлении, из профиля и с первого
 * касания избранного или уведомлений, а после входа закрывается сам.
 */
export function SignInSheet(props: { onClose: () => void }) {
  const strings = useStrings();
  return (
    <Sheet size="large" label={strings.signIn} onClose={props.onClose}>
      {(close) => <SignInForm close={close} />}
    </Sheet>
  );
}

function SignInForm({ close }: { close: () => void }) {
  const strings = useStrings();
  const lang = useLang();

  const [country, setCountry] = useState<PhoneCountry>(SERBIA);
  const [phoneDigits, setPhoneDigits] = useState('');
  const [code, setCode] = useState('');
  const [sentToPhone, setSentToPhone] = useState<string | null>(null);
  const [sentVia, setSentVia] = useState<string | null>(null);
  const [debugCode, setDebugCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [secondsToResend, setSecondsToResend] = useState(0);

  const busy = useRef(false);
  const phoneInput = useRef<HTMLInputElement>(null);
  const codeInput = useRef<HTMLInputElement>(null);

  const fullPhone = country.dialCode + phoneDigits;
  const canSendCode = phoneDigits.length >= 6;

  // Фокус следует за шагом: сначала номер, после отправки — код.
  useEffect(() => {
    (sentToPhone === null ? phoneInput : codeInput).current?.focus();
  }, [sentToPhone]);

  // Отсчёт до следующей отправки: сервер и так откажет раньше срока, но человеку лучше
  // узнать это до нажатия.
  const counting = secondsToResend > 0;
  useEffect(() => {
    if (!counting) return;
    const timer = setInterval(() => setSecondsToResend((seconds) => Math.max(0, seconds - 1)), 1_000);
    return () => clearInterval(timer);
  }, [counting]);

  const changePhone = (raw: string) => {
    // Автозаполнение вставляет номер целиком с кодом страны: код уходит в список, в поле
    // остаётся остальное.
    const full = splitPhone(raw);
    if (full) setCountry(full.country);
    setPhoneDigits((full ? full.digits : onlyDigits(raw)).slice(0, MAX_PHONE_DIGITS));
  };

  const run = async (action: () => Promise<void>, onApiError?: () => void) => {
    if (busy.current) return;
    busy.current = true;
    setIsBusy(true);
    setError(null);
    try {
      await action();
    } catch (failure) {
      setError(throttledTextOf(strings, failure) ?? errorText(strings, failure));
      if (failure instanceof ApiError) onApiError?.();
    } finally {
      busy.current = false;
      setIsBusy(false);
    }
  };

  const sendCode = (bySms = false) =>
    run(async () => {
      const requested = await requestCode(fullPhone, bySms);
      setDebugCode(requested.debugCode ?? null);
      setSentVia(messengerName(requested.channel));
      setSentToPhone(requested.phone);
      setSecondsToResend(requested.resendAfterSeconds ?? RESEND_COOLDOWN_S);
      codeInput.current?.focus();
    });

  const verify = (value: string) =>
    run(
      async () => {
        await verifyCode(fullPhone, value);
        close();
      },
      // Отвергнутый код стирается: следующий вводится с чистого поля.
      () => setCode(''),
    );

  const changeCode = (raw: string) => {
    const next = onlyDigits(raw).slice(0, CODE_LENGTH);
    setCode(next);
    if (next.length === CODE_LENGTH) void verify(next);
  };

  const changeNumber = () => {
    setSentToPhone(null);
    setSentVia(null);
    setCode('');
    setDebugCode(null);
    setError(null);
  };

  return (
    <>
      <div className="sheet-header">
        <GlassButton icon="close" label={strings.close} onClick={close} />
      </div>
      <div className="sheet-body">
        <div className="readable sheet-pad stack signin">
          <div className="stack signin-header">
            <BrandLockup />
            <h1 className="t-display">{sentToPhone === null ? strings.signInTitle : strings.codeTitle}</h1>
            <p className="t-subheadline t-muted">{sentToPhone === null ? strings.signInSubtitle : sentVia ? strings.codeSubtitleMessenger(sentVia, sentToPhone) : strings.codeSubtitle(sentToPhone)}</p>
          </div>

          {sentToPhone === null ? (
            <form
              key="phone"
              className="stack signin-step"
              onSubmit={(event) => {
                event.preventDefault();
                if (canSendCode) void sendCode();
              }}
            >
              <FieldBox label={strings.phoneLabel}>
                <CountryCodeMenu country={country} lang={lang} label={strings.phoneLabel} onChange={setCountry} />
                <input
                  ref={phoneInput}
                  className="t-numeric"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder={country === SERBIA ? strings.phonePlaceholder : ''}
                  value={phoneDigits}
                  onChange={(event) => changePhone(event.target.value)}
                />
              </FieldBox>
              <Button type="submit" disabled={!canSendCode || isBusy}>
                {strings.sendCode}
              </Button>
            </form>
          ) : (
            <form
              key="code"
              className="stack signin-step is-code"
              onSubmit={(event) => {
                event.preventDefault();
                if (code.length === CODE_LENGTH) void verify(code);
              }}
            >
              <FieldBox label={strings.codeHint}>
                <input
                  ref={codeInput}
                  className="t-numeric signin-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="······"
                  value={code}
                  onChange={(event) => changeCode(event.target.value)}
                />
              </FieldBox>

              {/* Мок-SMS отдаёт код в ответе: в разработке другого способа его узнать нет. */}
              {import.meta.env.DEV && debugCode && <p className="t-numeric t-footnote t-muted">debug: {debugCode}</p>}

              <Button type="submit" disabled={code.length !== CODE_LENGTH || isBusy}>
                {isBusy ? strings.loading : strings.confirm}
              </Button>

              {/* У номера нет мессенджера или сообщение задерживается: код приходит SMS сразу. */}
              {sentVia && (
                <Button type="button" kind="secondary" disabled={isBusy} onClick={() => void sendCode(true)}>
                  {strings.codeBySms}
                </Button>
              )}

              <div className="row signin-links">
                <button type="button" className="text-button t-numeric" disabled={secondsToResend > 0 || isBusy} onClick={() => void sendCode()}>
                  {secondsToResend > 0 ? strings.resendIn(secondsToResend) : strings.resend}
                </button>
                <button type="button" className="text-button signin-change" onClick={changeNumber}>
                  {strings.changeNumber}
                </button>
              </div>
            </form>
          )}

          {error && <ErrorBanner text={error} />}

          <p className="t-caption t-muted signin-legal">
            <LinkedText text={strings.legalNote(links.terms(lang), links.privacy(lang))} />
          </p>
        </div>
      </div>
    </>
  );
}

/** Код страны перед номером: системный список поверх подписи с флагом и кодом. */
